import { useMemo } from "react";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { WorkStatus, CatJobHistoryItem } from "../../../client/types.gen";
import { TooltipWrapper } from "../../../components/tooltip/TooltipWrapper";
import { formatAbbreviatedMoney, formatExactMoneyDollars } from "../Money";
import { formatDuration } from "../icm/util";
import { formatTimestamp } from "../utils";
import { SelectedModel } from "./types";
import { RunTags, hasPolicyTermRun } from "./RunTags";
import { canRerunFinancials } from "./runHistoryActions";
import styles from "./CatCard.module.css";

// Per-row recovery for the vendor models: resume a failed run, or re-run only
// the financial stage of a completed one. Each acts on that row's own batch.
// Null for models with neither. What resume does is the server's call — the
// pipelines pick up their failed stages in place, SpatialKat reprices its stored
// losses into a new batch — so the row rule here is the same either way.
// ``onRestart``: like resume, but failed chunks discard their potentially stuck
// request and submit fresh. ``actionInFlightBatchId``
// is the batch whose recovery is currently being kicked off (for the spinner + to disable the
// rest); ``disabled`` covers a run already in progress for this model.
interface EltRecoveryControls {
  onResume: (batchId: string) => void;
  onRestart?: (batchId: string) => void;
  onRerunFinancials: (batchId: string, activate: boolean) => void;
  shadowRerunAllowed: boolean;
  actionInFlightBatchId: string | null;
  disabled: boolean;
}

// Promote a completed run to the report's live results for this model, without
// re-running the vendor. Null unless the user is ENG and this model supports
// activation. ``inFlightBatchId`` is the batch being promoted (for the spinner
// + to disable the rest).
interface ActivationControls {
  onActivate: (batchId: string) => void;
  inFlightBatchId: string | null;
}

interface RunHistoryTabProps {
  jobHistory: CatJobHistoryItem[];
  selectedModel: SelectedModel;
  onDownloadAll: (job: CatJobHistoryItem) => void;
  // Open the side-by-side run comparison anchored on this run. Absent for
  // models with no financial-model audit.
  onCompareJob?: (job: CatJobHistoryItem) => void;
  eltRecovery?: EltRecoveryControls | null;
  activation?: ActivationControls | null;
}

const STATUS_LABEL: Partial<Record<WorkStatus, string>> = {
  [WorkStatus.DONE]: "Completed",
  [WorkStatus.FAILED]: "Failed",
};

// Status as a colored dot and word, then the tags that set the run apart.
const renderStatusCell = (job: CatJobHistoryItem, showTermTags: boolean) => {
  const isRunning =
    job.status === WorkStatus.PENDING || job.status === WorkStatus.IN_PROGRESS;
  const statusClass = isRunning
    ? styles.statusRunning
    : job.status === WorkStatus.DONE
      ? styles.statusCompleted
      : job.status === WorkStatus.FAILED
        ? styles.statusFailed
        : undefined;
  return (
    <span className={styles.statusCell}>
      <span className={`${styles.statusWord} ${statusClass ?? ""}`}>
        {isRunning ? (
          <ProgressSpinner className={styles.spinnerSmall} strokeWidth="4" />
        ) : (
          <span className={styles.statusDot} />
        )}
        {isRunning ? "In progress" : (STATUS_LABEL[job.status] ?? job.status)}
      </span>
      <RunTags job={job} showTermTags={showTermTags} />
    </span>
  );
};

// Whole-percent share, but keep a nonzero sliver visible rather than rounding
// it away to "0%".
const formatForeignShare = (foreignTiv: number, totalTiv: number): string => {
  const pct = (foreignTiv / totalTiv) * 100;
  if (pct > 0 && pct < 1) return "<1%";
  if (pct > 99 && pct < 100) return ">99%";
  return `${Math.round(pct)}%`;
};

// Total TIV is what the model saw, US + foreign. When a run has foreign
// exposure, break out that slice underneath. Runs from before the snapshot
// existed have a null ``foreign_tiv`` and read as they always did.
const renderTotalTivCell = (job: CatJobHistoryItem) => {
  const totalTiv = job.total_tiv;
  if (!totalTiv) return "—";

  const formattedTotal = formatExactMoneyDollars(totalTiv, {
    withRounding: true,
    withDollarSign: true,
  });
  const foreignTiv = job.foreign_tiv;
  // The share needs a total to divide by, so the subtext rides along with the
  // value rather than appearing under a "—".
  if (foreignTiv == null || foreignTiv <= 0) return formattedTotal;

  const exactForeign = formatExactMoneyDollars(foreignTiv, {
    withRounding: true,
    withDollarSign: true,
  });
  const exactDomestic = formatExactMoneyDollars(
    Math.max(totalTiv - foreignTiv, 0),
    { withRounding: true, withDollarSign: true }
  );

  return (
    <div>
      <div>{formattedTotal}</div>
      <TooltipWrapper
        tooltipText={`Of the TIV this run modeled, ${exactForeign} is foreign exposure (outside the 50 US states + DC) and ${exactDomestic} is US.`}
        maxWidth={260}
      >
        <span className={styles.foreignTivSubtext}>
          {formatAbbreviatedMoney(foreignTiv)} foreign ·{" "}
          {formatForeignShare(foreignTiv, totalTiv)}
        </span>
      </TooltipWrapper>
    </div>
  );
};

export const RunHistoryTab: React.FC<RunHistoryTabProps> = ({
  jobHistory,
  selectedModel,
  onDownloadAll,
  onCompareJob,
  eltRecovery,
  activation,
}) => {
  const filteredJobHistory = useMemo(
    () => jobHistory.filter((job) => job.type === selectedModel),
    [jobHistory, selectedModel]
  );

  // Comparison needs a second run to compare against, so the action only
  // appears once this model has at least two completed runs.
  const completedCount = useMemo(
    () =>
      filteredJobHistory.filter((job) => job.status === WorkStatus.DONE).length,
    [filteredJobHistory]
  );

  // Resume / restart recover an in-flight or failed pipeline, so they apply
  // only to the most recent batch — earlier batches are immutable history.
  // Pick the latest by ``started_at`` so the choice doesn't depend on whatever
  // order the parent merged the histories in.
  const latestBatchId = useMemo(() => {
    if (filteredJobHistory.length === 0) return null;
    return filteredJobHistory.reduce((latest, job) =>
      new Date(job.started_at).getTime() > new Date(latest.started_at).getTime()
        ? job
        : latest
    ).batch_id;
  }, [filteredJobHistory]);

  // Financial re-runs recompute from a completed batch's cached modeling and
  // emit a new batch, so they anchor on the batch backing the current results
  // — the active one. Otherwise a shadow run would steal the action from the
  // active batch by merely being more recent, blocking a fresh shadow re-run
  // of the active job. Fall back to the latest batch when nothing is active
  // yet (e.g. only shadow runs exist).
  const rerunAnchorBatchId = useMemo(
    () =>
      filteredJobHistory.find((job) => job.is_active)?.batch_id ??
      latestBatchId,
    [filteredJobHistory, latestBatchId]
  );

  const canRerun = (job: CatJobHistoryItem) =>
    canRerunFinancials(job, rerunAnchorBatchId);

  const showTermTags = hasPolicyTermRun(filteredJobHistory);

  if (filteredJobHistory.length === 0) {
    return (
      <Message
        severity="info"
        text="No job history available"
        className={styles.messageMargin}
      />
    );
  }

  return (
    <DataTable
      value={filteredJobHistory}
      sortField="started_at"
      sortOrder={-1}
      size="small"
      className={styles.resultsTable}
    >
      <Column
        field="status"
        header="Status"
        body={(job: CatJobHistoryItem) => renderStatusCell(job, showTermTags)}
        headerClassName={styles.statusColumn}
        align="left"
      />
      <Column
        field="started_at"
        header="Started"
        sortable
        body={(job: CatJobHistoryItem) => formatTimestamp(job.started_at)}
        headerClassName={styles.startedColumn}
        align="right"
      />
      <Column
        header="Duration"
        body={(job: CatJobHistoryItem) =>
          job.status === WorkStatus.DONE || job.status === WorkStatus.FAILED
            ? formatDuration(job.started_at, job.finished_at)
            : "—"
        }
        headerClassName={styles.countColumn}
        align="right"
      />
      <Column
        field="num_addresses"
        header="Addresses"
        body={(job: CatJobHistoryItem) =>
          job.num_addresses?.toLocaleString() ?? "—"
        }
        headerClassName={styles.countColumn}
        align="right"
      />
      <Column
        field="total_tiv"
        header="Total TIV"
        body={renderTotalTivCell}
        headerClassName={styles.moneyColumn}
        align="right"
      />
      <Column
        field="total_aal"
        header="Ground-up AAL"
        body={(job: CatJobHistoryItem) =>
          job.total_aal
            ? formatExactMoneyDollars(job.total_aal, {
                withRounding: true,
                withDollarSign: true,
              })
            : "—"
        }
        headerClassName={styles.moneyColumn}
        align="right"
      />
      <Column
        header=""
        body={(job: CatJobHistoryItem) => {
          // While any recovery is kicking off, disable the rest so the
          // user can't double-fire on the same model.
          const recoveryBusy =
            eltRecovery != null &&
            (eltRecovery.disabled || eltRecovery.actionInFlightBatchId != null);
          return (
            <div className={styles.actionButtons}>
              {eltRecovery &&
                job.batch_id === latestBatchId &&
                job.status === WorkStatus.FAILED && (
                  <Button
                    icon="pi pi-forward"
                    text
                    rounded
                    size="small"
                    severity="secondary"
                    tooltip="Resume this run from where it stalled"
                    tooltipOptions={{ position: "left" }}
                    loading={eltRecovery.actionInFlightBatchId === job.batch_id}
                    disabled={recoveryBusy}
                    onClick={() => eltRecovery.onResume(job.batch_id)}
                  />
                )}
              {eltRecovery?.onRestart &&
                job.batch_id === latestBatchId &&
                job.status === WorkStatus.FAILED && (
                  <Button
                    icon="pi pi-replay"
                    text
                    rounded
                    size="small"
                    severity="secondary"
                    tooltip="Restart this run: failed tasks restart their process with fresh requests to the modeling provider"
                    tooltipOptions={{ position: "left" }}
                    loading={eltRecovery.actionInFlightBatchId === job.batch_id}
                    disabled={recoveryBusy}
                    onClick={() => {
                      eltRecovery.onRestart?.(job.batch_id);
                    }}
                  />
                )}
              {eltRecovery && canRerun(job) && (
                <Button
                  icon="pi pi-refresh"
                  text
                  rounded
                  size="small"
                  severity="secondary"
                  tooltip="Re-run only the financial model, reusing this batch's cached modeling outputs"
                  tooltipOptions={{ position: "left" }}
                  loading={eltRecovery.actionInFlightBatchId === job.batch_id}
                  disabled={recoveryBusy}
                  onClick={() =>
                    eltRecovery.onRerunFinancials(job.batch_id, true)
                  }
                />
              )}
              {eltRecovery?.shadowRerunAllowed && canRerun(job) && (
                <Button
                  icon="pi pi-clone"
                  outlined
                  rounded
                  size="small"
                  severity="secondary"
                  tooltip="Re-runs financials without replacing the active results — inspect the batch in this table."
                  tooltipOptions={{ position: "left" }}
                  loading={eltRecovery.actionInFlightBatchId === job.batch_id}
                  disabled={recoveryBusy}
                  onClick={() =>
                    eltRecovery.onRerunFinancials(job.batch_id, false)
                  }
                />
              )}
              {activation &&
                job.status === WorkStatus.DONE &&
                !job.is_active && (
                  <Button
                    icon="pi pi-check-circle"
                    text
                    rounded
                    size="small"
                    severity="success"
                    tooltip="Make this run the active results for this model. Nothing is re-modeled, but staleness is measured against this run from now on, and an automatic reprice re-finances from its cached modeling."
                    tooltipOptions={{ position: "left" }}
                    loading={activation.inFlightBatchId === job.batch_id}
                    disabled={activation.inFlightBatchId != null}
                    onClick={() => activation.onActivate(job.batch_id)}
                  />
                )}
              {onCompareJob &&
                job.status === WorkStatus.DONE &&
                completedCount >= 2 && (
                  <Button
                    icon="pi pi-arrows-h"
                    text
                    rounded
                    size="small"
                    severity="secondary"
                    tooltip="Compare how losses flow into each layer against another run"
                    tooltipOptions={{ position: "left" }}
                    onClick={() => onCompareJob(job)}
                  />
                )}
              <Button
                icon="pi pi-download"
                text
                rounded
                size="small"
                severity="secondary"
                tooltip="Download all artifacts"
                tooltipOptions={{ position: "left" }}
                onClick={() => onDownloadAll(job)}
              />
            </div>
          );
        }}
        headerClassName={styles.actionsColumn}
        align="right"
      />
    </DataTable>
  );
};
